/**
 * Client-side match points formula for the picks page.
 * Prefers stored slot `ap` (API `augmentPoints`) for play contribution —
 * mirrors lambdas scoreCalculation attribution rules.
 */

import {
  applyConsensusMultiplier,
  formatConsensusMultiplierLabel,
  consensusMultiplierFromPickStats,
  isVoteBoosted,
  VOTE_BOOST_ADD
} from 'arena-shared/consensusMultiplier';
import {
  parseAugmentRef,
  isValidSplitPayload,
  SPLIT_TOTAL_WEIGHT,
  SACRIFICE_MULTIPLIER_FACTOR,
  isPenaltyVariant,
  isMissedPenaltyVariant,
  isRedCardVariant
} from './augments';

function roundMultiplier(value) {
  return Math.round(Number(value) * 10) / 10;
}

/** Same stack as GameStatsRecap.resolvePickPointsMultiplier (kept local to avoid circular imports). */
function resolvePickPointsMultiplier({
  pickStats,
  homeScore,
  awayScore,
  playerVotedForMatch = false,
  hasMultiplierAugment = false
}) {
  if (homeScore == null || awayScore == null) return null;
  const h = Number(homeScore);
  const a = Number(awayScore);
  if (!Number.isFinite(h) || !Number.isFinite(a)) return null;

  const voteBoosted = isVoteBoosted(pickStats);
  const voteAdd = voteBoosted && playerVotedForMatch ? VOTE_BOOST_ADD : 0;
  const base = pickStats
    ? consensusMultiplierFromPickStats(pickStats, homeScore, awayScore, false)
    : 1;
  const multiplierFactor = hasMultiplierAugment ? SACRIFICE_MULTIPLIER_FACTOR : 0;
  // ×1.7 applies to rarity only; community vote +0.1 stays outside the product.
  const total = roundMultiplier(
    multiplierFactor ? base * multiplierFactor + voteAdd : base + voteAdd
  );
  return {
    base,
    voteAdd,
    multiplierFactor,
    total,
    hasStack: voteAdd > 0 || multiplierFactor > 0
  };
}

export const OUTCOME_POINTS = 10;
export const EXACT_BONUS_POINTS = 10;
export const ONE_GOAL_OFF_BONUS = 6;
export const DRAW_NEAR_BONUS = 6;
export const CLOSE_MARGIN_BONUS = 4;
export const ONE_GOAL_CONSOLATION_POINTS = 10;
export const ONE_GOAL_POINTS = 10;

export const OUTCOME_ICON_SVG = '/outcome.svg';
export const EXACT_ICON_SVG = '/exact.svg';

function getOutcome(homeScore, awayScore) {
  if (homeScore > awayScore) return 'home';
  if (awayScore > homeScore) return 'away';
  return 'draw';
}

function getGoalDifference(homeScore, awayScore) {
  return homeScore - awayScore;
}

function scorelineDistance(predictedHome, predictedAway, actualHome, actualAway) {
  return Math.abs(predictedHome - actualHome) + Math.abs(predictedAway - actualAway);
}

function resolveRegularTimeScores(game, actualHome, actualAway) {
  if (game) {
    const h = game.homeScore ?? game.hs;
    const a = game.awayScore ?? game.as;
    if (h != null && a != null) return { home: h, away: a };
  }
  return { home: actualHome, away: actualAway };
}

function resolveScoreAfter120(game) {
  const h90 = game?.homeScore ?? game?.hs;
  const a90 = game?.awayScore ?? game?.as;
  const h12 = game?.scoreAfter120Home ?? game?.h12 ?? h90;
  const a12 = game?.scoreAfter120Away ?? game?.a12 ?? a90;
  return { home: h12, away: a12 };
}

function resolveMatchDuration(game) {
  const md = game?.matchDuration ?? game?.md;
  if (md === 'EXTRA_TIME' || md === 'et') return 'EXTRA_TIME';
  if (md === 'PENALTY_SHOOTOUT' || md === 'ps') return 'PENALTY_SHOOTOUT';
  return 'REGULAR';
}

/** @returns {{ outcome: number, precisionBonus: number, band: string|null, total: number }} */
export function calculateBetPointsComponents(predictedHome, predictedAway, actualHome, actualAway) {
  if (actualHome === null || actualAway === null || actualHome === undefined || actualAway === undefined) {
    return { outcome: 0, precisionBonus: 0, band: null, total: 0 };
  }

  const predOutcome = getOutcome(predictedHome, predictedAway);
  const actOutcome = getOutcome(actualHome, actualAway);
  if (predOutcome !== actOutcome) {
    return { outcome: 0, precisionBonus: 0, band: null, total: 0 };
  }

  const md = scorelineDistance(predictedHome, predictedAway, actualHome, actualAway);
  if (md === 0) {
    return {
      outcome: OUTCOME_POINTS,
      precisionBonus: EXACT_BONUS_POINTS,
      band: 'exact',
      total: OUTCOME_POINTS + EXACT_BONUS_POINTS
    };
  }

  let precisionBonus = 0;
  let band = 'result';
  if (actOutcome === 'draw') {
    if (md === 2) {
      precisionBonus = DRAW_NEAR_BONUS;
      band = 'draw_near';
    }
  } else if (md === 1) {
    precisionBonus = ONE_GOAL_OFF_BONUS;
    band = 'one_off';
  } else {
    const predGd = getGoalDifference(predictedHome, predictedAway);
    const actualGd = getGoalDifference(actualHome, actualAway);
    if (predGd === actualGd && Math.abs(actualGd) >= 1 && md === 2) {
      precisionBonus = CLOSE_MARGIN_BONUS;
      band = 'margin';
    }
  }

  return {
    outcome: OUTCOME_POINTS,
    precisionBonus,
    band,
    total: OUTCOME_POINTS + precisionBonus
  };
}

function splitLineSharePoints(predictedHome, predictedAway, actualHome, actualAway, weight) {
  if (actualHome == null || actualAway == null || !Number.isInteger(weight) || weight <= 0) {
    return 0;
  }
  const ladder = calculateBetPointsComponents(predictedHome, predictedAway, actualHome, actualAway)
    .total;
  return Math.floor((ladder * weight) / SPLIT_TOTAL_WEIGHT);
}

function iconOutcome() {
  return { type: 'outcome' };
}

function iconExact() {
  return { type: 'exact' };
}

function iconAugment(code, ref) {
  return { type: 'augment', code, ref: ref || code };
}

function iconConsensus() {
  return { type: 'consensus' };
}

function iconVote() {
  return { type: 'vote' };
}

function multiplierPart(resolved, forceMultiplierAugment = false) {
  if (!resolved) {
    return { value: 1, icons: [iconConsensus()], label: formatConsensusMultiplierLabel(1) };
  }
  const icons = [iconConsensus()];
  if (resolved.voteAdd > 0) icons.push(iconVote());
  if (resolved.multiplierFactor > 0 || forceMultiplierAugment) {
    icons.push(iconAugment('m', 'm'));
  }
  return {
    value: resolved.total,
    icons,
    label: formatConsensusMultiplierLabel(resolved.total)
  };
}

function addend(value, icons) {
  return { value, icons };
}

function isEventMatchAugment(code) {
  return (
    isPenaltyVariant(code) ||
    isMissedPenaltyVariant(code) ||
    isRedCardVariant(code) ||
    code === 'rc' ||
    code === 'h'
  );
}

function standardFromParts(addends, mult, consensusMultiplier, postMultiplierAddends = []) {
  const preMult = addends.reduce((sum, a) => sum + a.value, 0);
  const postMult = postMultiplierAddends.reduce((sum, a) => sum + a.value, 0);
  const total = applyConsensusMultiplier(preMult, consensusMultiplier) + postMult;
  return {
    mode: 'standard',
    addends,
    postMultiplierAddends,
    multiplier: mult,
    total
  };
}

function ladderAddends(base) {
  const addends = [];
  if (base.outcome > 0) addends.push(addend(base.outcome, [iconOutcome()]));
  return addends;
}

function precisionPostAddends(base) {
  if (base.precisionBonus > 0) return [addend(base.precisionBonus, [iconExact()])];
  return [];
}

function standardLadderFromParts(base, mult, consensusMultiplier, extraPre = [], extraPost = []) {
  return standardFromParts(
    [...ladderAddends(base), ...extraPre],
    mult,
    consensusMultiplier,
    [...precisionPostAddends(base), ...extraPost]
  );
}

function splitLineShareParts(predictedHome, predictedAway, actualHome, actualAway, weight) {
  if (actualHome == null || actualAway == null || !Number.isInteger(weight) || weight <= 0) {
    return { outcomeShare: 0, precisionShare: 0, totalShare: 0 };
  }
  const comps = calculateBetPointsComponents(predictedHome, predictedAway, actualHome, actualAway);
  if (comps.total <= 0) return { outcomeShare: 0, precisionShare: 0, totalShare: 0 };
  const outcomeShare = Math.floor((comps.outcome * weight) / SPLIT_TOTAL_WEIGHT);
  const precisionShare = Math.floor((comps.precisionBonus * weight) / SPLIT_TOTAL_WEIGHT);
  return { outcomeShare, precisionShare, totalShare: outcomeShare + precisionShare };
}

/**
 * Build a display breakdown for a finished pick.
 * Prefer stored `augmentPoints` (slot `ap`) for play contribution.
 * Multiplier: never show ap — only the × mult icons (incl. m).
 *
 * @param {object} args
 * @param {boolean|null|undefined} [args.augmentApplied] — API flag (derived from ap/ar / Sacrifice)
 * @param {number|null|undefined} [args.augmentPoints] — slot `ap`
 */
export function buildMatchPointsBreakdown({
  predictedHome,
  predictedAway,
  game,
  augment,
  pickStats,
  playerVotedForMatch = false,
  expectedPoints = null,
  augmentApplied = null,
  augmentPoints = null
}) {
  if (predictedHome == null || predictedAway == null) return null;
  if (game?.homeScore == null || game?.awayScore == null) return null;

  const augmentRef = augment != null && augment !== '' ? String(augment) : '';
  const { code: augmentCode, split } = augmentRef
    ? parseAugmentRef(augmentRef)
    : { code: '', split: null };

  const storedAp =
    augmentPoints != null && Number.isFinite(Number(augmentPoints))
      ? Number(augmentPoints)
      : null;

  const applied = augmentApplied === true;
  const missed = augmentApplied === false;
  /** Show play icon + ap term (never for Multiplier — ap is attribution only). */
  const showApTerm =
    !missed &&
    storedAp != null &&
    storedAp !== 0 &&
    augmentCode !== 'm' &&
    augmentCode !== 'd';

  const hasMultiplierAugment = !missed && augmentCode === 'm' && (applied || augmentApplied == null);
  const resolvedMult = resolvePickPointsMultiplier({
    pickStats,
    homeScore: game.homeScore,
    awayScore: game.awayScore,
    playerVotedForMatch,
    hasMultiplierAugment
  });
  const mult = multiplierPart(
    resolvedMult ?? {
      base: 1,
      voteAdd: 0,
      multiplierFactor: hasMultiplierAugment ? SACRIFICE_MULTIPLIER_FACTOR : 0,
      total: hasMultiplierAugment ? SACRIFICE_MULTIPLIER_FACTOR : 1,
      hasStack: hasMultiplierAugment
    },
    hasMultiplierAugment && (applied || augmentApplied == null)
  );
  const consensusMultiplier = mult.value;

  const finish = (breakdown) => {
    if (expectedPoints != null && Number(expectedPoints) !== Number(breakdown.total)) {
      return null;
    }
    return breakdown;
  };

  // --- Sacrifice: no ap ---
  if (augmentCode === 'd') {
    return finish({
      mode: 'flat',
      flat: {
        value: 0,
        icons: applied || augmentApplied == null ? [iconAugment('d', augmentRef)] : []
      },
      total: 0
    });
  }

  // --- One Goal: wrong 1×2 + MD === 1 → flat 10; correct + one_off → exact (20) ---
  if (augmentCode === 'g' || augmentCode === '+1h' || augmentCode === '+1a') {
    const reg = resolveRegularTimeScores(game, game.homeScore, game.awayScore);
    const base = calculateBetPointsComponents(
      predictedHome,
      predictedAway,
      reg.home,
      reg.away
    );
    if (base.total > 0) {
      if (base.band === 'one_off') {
        const upgraded = {
          outcome: OUTCOME_POINTS,
          precisionBonus: EXACT_BONUS_POINTS,
          band: 'exact',
          total: OUTCOME_POINTS + EXACT_BONUS_POINTS
        };
        const showG = applied || showApTerm || augmentApplied == null;
        const post = [
          addend(EXACT_BONUS_POINTS, [
            iconExact(),
            ...(showG ? [iconAugment(augmentCode, augmentRef)] : [])
          ])
        ];
        return finish(
          standardFromParts(ladderAddends(upgraded), mult, consensusMultiplier, post)
        );
      }
      return finish(standardLadderFromParts(base, mult, consensusMultiplier));
    }
    if (showApTerm) {
      return finish({
        mode: 'flat',
        flat: {
          value: storedAp,
          icons: [iconAugment(augmentCode, augmentRef)]
        },
        total: storedAp
      });
    }
    return null;
  }

  // --- Split: both lines weighted; take better share; rarity scales outcome share only ---
  // Display like other ladders: (outcomeShare × rarity) + precisionShare — not totalShare × rarity.
  if (augmentCode === 's') {
    const reg = resolveRegularTimeScores(game, game.homeScore, game.awayScore);
    if (!isValidSplitPayload(split)) return null;
    const w2 = split.weight;
    const w1 = SPLIT_TOTAL_WEIGHT - w2;
    const primary = splitLineShareParts(
      predictedHome,
      predictedAway,
      reg.home,
      reg.away,
      w1
    );
    const secondary = !missed
      ? splitLineShareParts(split.home, split.away, reg.home, reg.away, w2)
      : { outcomeShare: 0, precisionShare: 0, totalShare: 0 };
    const secondaryShare = showApTerm ? storedAp : secondary.totalShare;
    const usePrimary = primary.totalShare >= (secondaryShare || 0);
    const best = usePrimary ? primary : secondary;
    const bestShare = usePrimary ? primary.totalShare : secondaryShare;
    if ((bestShare || 0) <= 0) return null;
    const addends = [];
    if (best.outcomeShare > 0) {
      addends.push(
        addend(best.outcomeShare, [iconOutcome(), iconAugment('s', augmentRef)])
      );
    }
    const post = [];
    if (best.precisionShare > 0) {
      post.push(addend(best.precisionShare, [iconExact()]));
    }
    if (addends.length === 0 && post.length === 0) return null;
    return finish(standardFromParts(addends, mult, consensusMultiplier, post));
  }

  // --- Extra time: ladder at 120'; rarity scales outcome only ---
  if (augmentCode === 'et') {
    const duration = resolveMatchDuration(game);
    if (duration === 'REGULAR') return null;
    const { home: actH120, away: actA120 } = resolveScoreAfter120(game);
    if (actH120 == null || actA120 == null) return null;
    const baseAt120 = calculateBetPointsComponents(
      predictedHome,
      predictedAway,
      actH120,
      actA120
    );
    if (baseAt120.total > 0) {
      return finish(standardLadderFromParts(baseAt120, mult, consensusMultiplier));
    }
    if (showApTerm) {
      return finish(
        standardFromParts(
          [addend(storedAp, [iconAugment('et', augmentRef)])],
          mult,
          consensusMultiplier
        )
      );
    }
    return null;
  }

  // --- Shootout: ladder at 120 + winner flat; rarity scales outcome only ---
  if (augmentCode === 'ph' || augmentCode === 'pa') {
    if (predictedHome !== predictedAway) return null;
    const { home: actH120, away: actA120 } = resolveScoreAfter120(game);
    if (actH120 == null || actA120 == null || actH120 !== actA120) return null;
    const baseAt120 = calculateBetPointsComponents(
      predictedHome,
      predictedAway,
      actH120,
      actA120
    );
    const postMultiplierAddends = [];
    if (showApTerm && storedAp != null && baseAt120.total > 0) {
      const winnerFlat = storedAp - baseAt120.total;
      if (winnerFlat > 0) {
        postMultiplierAddends.push(addend(winnerFlat, [iconAugment(augmentCode, augmentRef)]));
      }
    } else if (showApTerm && baseAt120.total <= 0) {
      return finish(
        standardFromParts(
          [addend(storedAp, [iconAugment(augmentCode, augmentRef)])],
          mult,
          consensusMultiplier
        )
      );
    }
    if (baseAt120.total <= 0 && postMultiplierAddends.length === 0) return null;
    return finish(
      standardLadderFromParts(baseAt120, mult, consensusMultiplier, [], postMultiplierAddends)
    );
  }

  // --- Bounty: outcome × rarity + precision flat + staked flat when 1×2 correct ---
  if (augmentCode === 'b') {
    const reg = resolveRegularTimeScores(game, game.homeScore, game.awayScore);
    const base = calculateBetPointsComponents(
      predictedHome,
      predictedAway,
      reg.home,
      reg.away
    );
    if (base.total <= 0 && !showApTerm) {
      return finish({
        mode: 'flat',
        flat: { value: 0, icons: missed ? [iconAugment('b', augmentRef)] : [] },
        total: 0
      });
    }
    const postMultiplierAddends = [];
    if (showApTerm) {
      postMultiplierAddends.push(addend(storedAp, [iconAugment('b', augmentRef)]));
    }
    return finish(
      standardLadderFromParts(base, mult, consensusMultiplier, [], postMultiplierAddends)
    );
  }

  // --- Hourglass: outcome × rarity + precision flat + remaining flat when 1×2 correct ---
  if (augmentCode === 'h') {
    const reg = resolveRegularTimeScores(game, game.homeScore, game.awayScore);
    const base = calculateBetPointsComponents(
      predictedHome,
      predictedAway,
      reg.home,
      reg.away
    );
    if (base.total <= 0 && !showApTerm) {
      return finish({
        mode: 'flat',
        flat: { value: 0, icons: missed ? [iconAugment('h', augmentRef)] : [] },
        total: 0
      });
    }
    const postMultiplierAddends = [];
    if (showApTerm) {
      postMultiplierAddends.push(addend(storedAp, [iconAugment('h', augmentRef)]));
    }
    return finish(
      standardLadderFromParts(base, mult, consensusMultiplier, [], postMultiplierAddends)
    );
  }

  // --- Parlay: outcome × rarity + precision flat + pair bonus ---
  if (augmentCode === 'p') {
    const reg = resolveRegularTimeScores(game, game.homeScore, game.awayScore);
    const base = calculateBetPointsComponents(
      predictedHome,
      predictedAway,
      reg.home,
      reg.away
    );
    const postMultiplierAddends = [];
    if (showApTerm) {
      postMultiplierAddends.push(addend(storedAp, [iconAugment('p', augmentRef)]));
    }
    if (base.total <= 0 && postMultiplierAddends.length === 0) {
      return finish({
        mode: 'flat',
        flat: { value: 0, icons: missed ? [iconAugment('p', augmentRef)] : [] },
        total: 0
      });
    }
    if (base.total <= 0) {
      return finish({
        mode: 'flat',
        flat: {
          value: storedAp,
          icons: [iconAugment('p', augmentRef)]
        },
        total: storedAp
      });
    }
    return finish(
      standardLadderFromParts(base, mult, consensusMultiplier, [], postMultiplierAddends)
    );
  }

  // --- Double or bust (`2x`): force 2.0× (2.1× with vote); bust → flat penalty (not applied) ---
  if (augmentCode === '2x') {
    const reg = resolveRegularTimeScores(game, game.homeScore, game.awayScore);
    const base = calculateBetPointsComponents(predictedHome, predictedAway, reg.home, reg.away);
    if (base.total <= 0) {
      // Prefer stored negative ap (legacy rows that marked bust as applied); else bet points.
      const bustPenalty =
        storedAp != null && storedAp < 0
          ? storedAp
          : expectedPoints != null && Number(expectedPoints) < 0
            ? Number(expectedPoints)
            : null;
      if (bustPenalty != null) {
        return finish({
          mode: 'flat',
          flat: { value: bustPenalty, icons: [iconAugment('2x', augmentRef)] },
          total: bustPenalty
        });
      }
      return null;
    }
    if (missed) {
      return finish(standardLadderFromParts(base, mult, consensusMultiplier));
    }
    const voteBoosted = isVoteBoosted(pickStats);
    const voteAdd = voteBoosted && playerVotedForMatch ? VOTE_BOOST_ADD : 0;
    const forcedTotal = roundMultiplier(2 + voteAdd);
    const forcedMult = {
      value: forcedTotal,
      icons: [
        iconConsensus(),
        iconAugment('2x', augmentRef),
        ...(voteAdd > 0 ? [iconVote()] : [])
      ],
      label: formatConsensusMultiplierLabel(forcedTotal)
    };
    return finish(standardLadderFromParts(base, forcedMult, forcedTotal));
  }

  // --- Standard 90' (gd / events / m / none) ---
  // ap = pre-consensus delta (not shown for Multiplier). Precision is post-rarity.
  const reg = resolveRegularTimeScores(game, game.homeScore, game.awayScore);
  const base = calculateBetPointsComponents(predictedHome, predictedAway, reg.home, reg.away);
  const extraPre = [];
  const postMultiplierAddends = [];
  if (showApTerm) {
    const apTerm = addend(storedAp, [iconAugment(augmentCode, augmentRef)]);
    if (isEventMatchAugment(augmentCode)) {
      postMultiplierAddends.push(apTerm);
    } else {
      extraPre.push(apTerm);
    }
  }

  if (base.outcome <= 0 && base.precisionBonus <= 0 && extraPre.length === 0 && postMultiplierAddends.length === 0) {
    return null;
  }
  return finish(
    standardLadderFromParts(base, mult, consensusMultiplier, extraPre, postMultiplierAddends)
  );
}
